
import { Component, Vue } from "vue-property-decorator";
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import { OSM, XYZ } from "ol/source";
import DestinationLayer from "./Layers/DestinationLayer";
import RouteWithTrips from "@/components/Passenger/models/RouteWithTrips";
import { fromLonLat } from "ol/proj";
import RouteLayer from "./Layers/RouteLayer";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import CarLiveTrackingControl from "./Controls/CarLiveTrackingControl";
import CarLayer from "./Layers/CarLayer";
import DestinationFeature from "./Features/DestinationFeature";

export enum LayerType {
  Car,
  Destination,
  Route,
}

@Component
export default class TimitMap extends Vue {
  map: Map | null = null;
  private carLiveTrackingControl: CarLiveTrackingControl | null = null;
  private carLayer: CarLayer | null = null;
  private destinationLayer: DestinationLayer | null = null;
  private routeLayer: RouteLayer | null = null;

  mounted() {
    this.map = new Map({
      target: 'map',
      view: new View({
        center: fromLonLat([24.945831, 60.192059]),
        zoom: 10,
      }),
      layers: [
        new TileLayer({
          source: this.getTileLayer(),
        }),
      ],
    });
    this.map.on('click', (event) => this.handleClick(event));
  }

  getTileLayer() {
    const useDigitransitMap = ['timit.fi', 'localhost'].some(domain => window.location.host.toLocaleLowerCase().includes(domain));
    const apiKey = process.env.VUE_APP_DIGITRANSIT_SUBSCRIPTION_KEY ?? null;
    if (apiKey && useDigitransitMap) {
      return new XYZ({
        url: `https://cdn.digitransit.fi/map/v3/hsl-map/{z}/{x}/{y}.png?digitransit-subscription-key=${apiKey}`,
        minZoom: 1,
        maxZoom: 18,
        tileSize: 512,
        attributions: 'Digitransit'
      });
    }
    return new OSM();
  }

  private centerMap(layer: VectorLayer<VectorSource>) {
    const extent = layer.getSource().getExtent();
    this.map.getView().fit(extent, { padding: [50, 50, 50, 50] });
  }

  private handleClick(event) {
    this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
      if (feature instanceof DestinationFeature) {
        this.$emit("selectDestination", (feature as DestinationFeature).destination);
        return true;
      }
    });
  }

  // #region Map element creation
  private createDestinationLayer() {
    if (!this.destinationLayer) {
      this.destinationLayer = new DestinationLayer();
      this.map.addLayer(this.destinationLayer);
    }
  }

  private createRouteLayer() {
    if (!this.routeLayer) {
      this.routeLayer = new RouteLayer();
      this.map.addLayer(this.routeLayer);
    }
  }

  private createCarLayer(route: RouteWithTrips, tripId: number) {
    if (!this.carLayer) {
      this.carLayer = new CarLayer(route, tripId);
      this.map.addLayer(this.carLayer);
    }
  }

  private createCarLiveTrackingControl() {
    if (!this.carLiveTrackingControl) {
      this.carLiveTrackingControl = new CarLiveTrackingControl();
      this.map.addControl(this.carLiveTrackingControl);
      this.carLiveTrackingControl.on('change', () => this.updateLiveLocation());
    }
  }
  // #endregion

  clearMap() {
    if (this.carLiveTrackingControl?.liveTracking) {
      this.carLayer?.stopUpdatingLiveLocation();
    }
  }

  changeLayerVisibility(layer: LayerType) {
    if (layer == LayerType.Car) {
      this.carLayer?.setVisible(!this.carLayer?.getVisible());
      this.carLiveTrackingControl?.changeBtnVisibility();
    } else if (layer == LayerType.Destination) {
      this.destinationLayer?.setVisible(!this.destinationLayer?.getVisible());
    } else if (layer == LayerType.Route) {
      this.routeLayer?.setVisible(!this.routeLayer?.getVisible());
    }
  }

  showRouteWithDestinations(route: RouteWithTrips, tripId: number) {
    if (route.destinations?.length > 0) {
      this.createDestinationLayer();
      this.destinationLayer?.showDestinations(route);
      this.destinationLayer?.selectDestinationByTripId(tripId);
    }
    if (route.osrmRouteGeometry) {
      this.createRouteLayer();
      this.routeLayer?.showRoute(route.osrmRouteGeometry);
    }
    if (route.liveLocationAvailable || route.drivenRoute?.length > 1) {
      this.createCarLayer(route, tripId);
      this.carLayer?.showDrivenRoute(route.drivenRoute);
    }
    if (route.liveLocationAvailable) {
      this.createCarLiveTrackingControl();
      this.carLiveTrackingControl?.handleTrackCar();
    }
    this.centerMap(this.routeLayer || this.destinationLayer);
  }

  selectDestinationByTripId(tripId: number) {
    this.createDestinationLayer();
    this.destinationLayer?.selectDestinationByTripId(tripId);
  }

  updateLiveLocation() {
    if (this.carLiveTrackingControl?.liveTracking) {
      this.carLayer?.startUpdatingLiveLocation();
    } else {
      this.carLayer?.stopUpdatingLiveLocation();
    }
  }
}
